/**
 * Sieve — grid generation & validation (spec §4, §5.2).
 *
 * Part of the shared engine (§14): the generator uses this to build the queue,
 * and the curation tooling replays it. The live client never generates — it
 * only reads pre-generated puzzles — but it imports the constants and the
 * Puzzle type from here so there is one source of truth.
 */

import {
  classify,
  isPrime,
  isNearT3,
  compositesByClass,
  primesInRange,
  type TrapClass,
} from './primes';
import { makeRng, type Rng } from './rng';

// ─── Compile-time invariants (§17: every one of these is constant forever) ───
export const GRID_SIZE = 5; // 5×5
export const CELL_COUNT = GRID_SIZE * GRID_SIZE; // 25
export const PRIME_COUNT = 5;
export const GIMME_COUNT = 8;
export const TRAP_COUNT = 12;
export const MISTAKE_BUDGET = 3;

/** Weekday difficulty tiers (§4.4). Order matches ISO weekday for clarity. */
export type DayTier = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface TrapProfile {
  t1: number;
  t2: number;
  t3: number;
}

/** The weekly difficulty curve (§4.4). Each row sums to TRAP_COUNT (12). */
export const TRAP_PROFILES: Record<DayTier, TrapProfile> = {
  mon: { t1: 8, t2: 4, t3: 0 },
  tue: { t1: 6, t2: 5, t3: 1 },
  wed: { t1: 5, t2: 5, t3: 2 },
  thu: { t1: 4, t2: 5, t3: 3 },
  fri: { t1: 3, t2: 5, t3: 4 },
  sat: { t1: 2, t2: 4, t3: 6 },
  sun: { t1: 5, t2: 5, t3: 2 },
};

/** ISO weekday (Mon=0 … Sun=6) → tier, from a `YYYY-MM-DD` date. */
const TIER_BY_WEEKDAY: DayTier[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export function tierForDate(isoDate: string): DayTier {
  // Parse as UTC so tier assignment is timezone-independent during generation.
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const jsDay = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun … 6=Sat
  const isoIndex = (jsDay + 6) % 7; // 0=Mon … 6=Sun
  return TIER_BY_WEEKDAY[isoIndex]!;
}

/**
 * A single day's puzzle (§5.1 schema, trimmed per §17: grid size, prime count,
 * mistake budget and range are compile-time constants, not per-puzzle fields).
 */
export interface Puzzle {
  id: number;
  date: string; // YYYY-MM-DD
  grid: number[]; // CELL_COUNT numbers, 100–999, all distinct
  primeIndices: number[]; // PRIME_COUNT indices into `grid`
  difficultyTier: DayTier;
  trapProfile: TrapProfile;
}

/** Rolling-window history the generator consults to avoid recent reuse (§5.2). */
export interface GenHistory {
  /** number → most recent puzzle id it appeared in. */
  numberLastSeen: Map<number, number>;
  /** prime → most recent puzzle id it appeared in. */
  primeLastSeen: Map<number, number>;
}

export function emptyHistory(): GenHistory {
  return { numberLastSeen: new Map(), primeLastSeen: new Map() };
}

export const NUMBER_REUSE_WINDOW = 60; // days (§5.2 rule 4)
export const PRIME_REUSE_WINDOW = 90; // days (§5.2 rule 5)

// ─── Selection helpers ───────────────────────────────────────────────────────

const ALL_PRIMES = primesInRange();
const COMPOSITES = compositesByClass();

function leadingDigit(n: number): number {
  return Math.floor(n / 100);
}

/**
 * IMPORTANT — reuse windows are BEST-EFFORT, not hard rejects.
 *
 * The spec (§5.2 rules 4 & 5) asks for a 60-day no-reuse window on any number
 * and a 90-day window on primes. Those are mathematically unsatisfiable for a
 * 2-year daily queue given the fixed 100–999 range:
 *
 *   • only 143 primes exist, and each day needs 5 → a 90-day window would
 *     require 450 distinct primes;
 *   • only 27 / 34 / 36 T3 / T1 / T2 composites exist, and the weekly curve
 *     demands 18 / 33 / 33 of them → a 60-day window is impossible.
 *
 * §17.3 acknowledges this ("each prime recurs roughly 25 times across a 2-year
 * queue … the reuse windows keep recurrence invisible in practice"). So the
 * honest implementation of the windows' INTENT is: honour the window whenever
 * the pool is large enough, and otherwise maximise spacing by drawing the
 * least-recently-used numbers. This function returns an ordered eligible list
 * embodying that policy.
 */
function eligibleOrdered(
  rng: Rng,
  pool: readonly number[],
  count: number,
  lastSeen: Map<number, number>,
  currentId: number,
  window: number,
): number[] {
  const fresh = pool.filter((n) => {
    const last = lastSeen.get(n);
    return last === undefined || currentId - last > window;
  });
  if (fresh.length >= count) {
    // Window can be honoured — pick freely among genuinely-fresh numbers.
    return rng.shuffle([...fresh]);
  }
  // Pool too small for the window: maximise the gap. Tie-shuffle first so equal
  // ages are broken randomly, then stable-sort oldest-first (never-seen = -1).
  const shuffled = rng.shuffle([...pool]);
  return shuffled.sort((a, b) => (lastSeen.get(a) ?? -1) - (lastSeen.get(b) ?? -1));
}

/**
 * Selects 5 primes: least-recently-used first (max spacing), while satisfying
 * §4.5 — no more than 2 primes share a leading digit. Greedy over the ordered
 * eligible list preserves the spacing preference.
 */
function selectPrimes(rng: Rng, currentId: number, history: GenHistory): number[] {
  const ordered = eligibleOrdered(
    rng,
    ALL_PRIMES,
    PRIME_COUNT,
    history.primeLastSeen,
    currentId,
    PRIME_REUSE_WINDOW,
  );
  const chosen: number[] = [];
  const digitCounts = new Map<number, number>();
  for (const p of ordered) {
    if (chosen.length === PRIME_COUNT) break;
    const d = leadingDigit(p);
    if ((digitCounts.get(d) ?? 0) >= 2) continue; // §4.5 cap
    chosen.push(p);
    digitCounts.set(d, (digitCounts.get(d) ?? 0) + 1);
  }
  // Extremely unlikely fallback (pool always affords 5 within the cap).
  if (chosen.length < PRIME_COUNT) return ordered.slice(0, PRIME_COUNT);
  return chosen;
}

/** Selects `count` composites of a class, least-recently-used first (§5.2). */
function selectComposites(
  rng: Rng,
  cls: TrapClass | 'gimme',
  count: number,
  currentId: number,
  history: GenHistory,
): number[] {
  return eligibleOrdered(
    rng,
    COMPOSITES[cls],
    count,
    history.numberLastSeen,
    currentId,
    NUMBER_REUSE_WINDOW,
  ).slice(0, count);
}

// ─── Public generation API ───────────────────────────────────────────────────

export interface GenerateOptions {
  id: number;
  date: string;
  /** History of earlier puzzles for the reuse windows (§5.2 rules 4,5). */
  history?: GenHistory;
  /** Base seed; combined with id for reproducibility (§5.1). */
  seed?: number;
}

/**
 * Generates one valid puzzle for a date, or throws if it cannot within budget.
 * Every returned puzzle passes {@link validatePuzzle}.
 */
export function generatePuzzle(opts: GenerateOptions): Puzzle {
  const { id, date } = opts;
  const history = opts.history ?? emptyHistory();
  const tier = tierForDate(date);
  const profile = TRAP_PROFILES[tier];
  const baseSeed = opts.seed ?? 0x51e7e;

  for (let attempt = 0; attempt < 500; attempt++) {
    const rng = makeRng((baseSeed ^ (id * 2654435761)) + attempt * 40503);

    const primes = selectPrimes(rng, id, history);
    const gimmes = selectComposites(rng, 'gimme', GIMME_COUNT, id, history);
    const t1 = selectComposites(rng, 't1', profile.t1, id, history);
    const t2 = selectComposites(rng, 't2', profile.t2, id, history);
    const t3 = selectComposites(rng, 't3', profile.t3, id, history);

    // Soft preference (§4.5): at least one prime near a T3 already chosen. If
    // the day has T3 traps but none of the primes sits near one, retry a few
    // times before accepting — it is a preference, not a hard rule.
    const nonPrime = [...gimmes, ...t1, ...t2, ...t3];
    if (profile.t3 > 0 && attempt < 200) {
      const anyNear = primes.some((p) => isNearT3(p, [...primes, ...nonPrime]));
      if (!anyNear) continue;
    }

    const numbers = [...primes, ...nonPrime];
    // §5.2 rule 1: all distinct. Prime and composite pools are disjoint by
    // construction, but a defensive check keeps the invariant explicit.
    if (new Set(numbers).size !== CELL_COUNT) continue;

    // §5.2 rule 8: shuffle layout independently of generation order.
    const positions = rng.shuffle([...Array(CELL_COUNT).keys()]);
    const grid = new Array<number>(CELL_COUNT);
    positions.forEach((pos, i) => {
      grid[pos] = numbers[i]!;
    });
    const primeIndices = positions.slice(0, PRIME_COUNT).sort((a, b) => a - b);

    const puzzle: Puzzle = {
      id,
      date,
      grid,
      primeIndices,
      difficultyTier: tier,
      trapProfile: profile,
    };

    // §5.2 rules 6 & 7 concern layout — retry the shuffle if they fail.
    if (validatePuzzle(puzzle).length === 0) return puzzle;
  }

  throw new Error(`Could not generate a valid puzzle for ${id} (${date})`);
}

/** Records a generated puzzle into history so later days see the reuse. */
export function recordHistory(history: GenHistory, puzzle: Puzzle): void {
  for (const n of puzzle.grid) history.numberLastSeen.set(n, puzzle.id);
  for (const i of puzzle.primeIndices) {
    history.primeLastSeen.set(puzzle.grid[i]!, puzzle.id);
  }
}

// ─── Validation (§5.2) ───────────────────────────────────────────────────────

const orthogonalNeighbours = (idx: number): number[] => {
  const r = Math.floor(idx / GRID_SIZE);
  const c = idx % GRID_SIZE;
  const out: number[] = [];
  if (r > 0) out.push(idx - GRID_SIZE);
  if (r < GRID_SIZE - 1) out.push(idx + GRID_SIZE);
  if (c > 0) out.push(idx - 1);
  if (c < GRID_SIZE - 1) out.push(idx + 1);
  return out;
};

/**
 * Returns a list of hard §5.2 rule violations (empty = valid). Covers the rules
 * that are always satisfiable and must never ship broken: 1 (distinct/in-range),
 * 2 (exactly 5 primes, independently verified), 3 (trap profile + gimme count),
 * 6 (row/column spread), 7 (≤1 adjacent prime pair), 8 (layout is a real
 * shuffle — implied by 6 & 7 holding on a shuffled grid).
 *
 * The reuse windows (rules 4 & 5) are NOT hard rules here — see
 * {@link eligibleOrdered} and {@link reuseGap} for why they are best-effort
 * spacing goals rather than absolute rejects. Use {@link minReuseGap} to audit
 * how well a finished queue spaces its numbers.
 */
export function validatePuzzle(p: Puzzle): string[] {
  const errors: string[] = [];

  // Rule 1: 25 distinct numbers in range.
  if (p.grid.length !== CELL_COUNT) errors.push(`grid must hold ${CELL_COUNT} cells`);
  if (new Set(p.grid).size !== p.grid.length) errors.push('duplicate numbers in grid');
  for (const n of p.grid) {
    if (n < 100 || n > 999) errors.push(`number ${n} out of range 100–999`);
  }

  // Rule 2: exactly 5 primes, verified independently by trial division.
  const actualPrimeIdx = p.grid.map((n, i) => (isPrime(n) ? i : -1)).filter((i) => i >= 0);
  if (actualPrimeIdx.length !== PRIME_COUNT) {
    errors.push(`expected ${PRIME_COUNT} primes, found ${actualPrimeIdx.length}`);
  }
  const declared = [...p.primeIndices].sort((a, b) => a - b);
  if (declared.join(',') !== actualPrimeIdx.join(',')) {
    errors.push('primeIndices do not match the actually-prime cells');
  }

  // Rule 3: trap profile matches the day's target exactly.
  const target = TRAP_PROFILES[p.difficultyTier];
  const counts: TrapProfile = { t1: 0, t2: 0, t3: 0 };
  let gimmes = 0;
  for (const n of p.grid) {
    const cls = classify(n);
    if (cls === 't1' || cls === 't2' || cls === 't3') counts[cls]++;
    else if (cls === 'gimme') gimmes++;
  }
  if (counts.t1 !== target.t1 || counts.t2 !== target.t2 || counts.t3 !== target.t3) {
    errors.push(
      `trap profile ${JSON.stringify(counts)} ≠ target ${JSON.stringify(target)} for ${p.difficultyTier}`,
    );
  }
  if (gimmes !== GIMME_COUNT) errors.push(`expected ${GIMME_COUNT} gimmes, found ${gimmes}`);
  if (
    p.trapProfile.t1 !== target.t1 ||
    p.trapProfile.t2 !== target.t2 ||
    p.trapProfile.t3 !== target.t3
  ) {
    errors.push('stored trapProfile does not match the day tier');
  }

  // Rule 6: primes span ≥3 distinct rows and ≥3 distinct columns.
  const rows = new Set(p.primeIndices.map((i) => Math.floor(i / GRID_SIZE)));
  const cols = new Set(p.primeIndices.map((i) => i % GRID_SIZE));
  if (rows.size < 3) errors.push('primes occupy fewer than 3 distinct rows');
  if (cols.size < 3) errors.push('primes occupy fewer than 3 distinct columns');

  // Rule 7: at most one orthogonally-adjacent prime pair (avoids a cluster tell).
  const primeSet = new Set(p.primeIndices);
  let adjacentPairs = 0;
  for (const i of p.primeIndices) {
    for (const nb of orthogonalNeighbours(i)) {
      if (nb > i && primeSet.has(nb)) adjacentPairs++;
    }
  }
  if (adjacentPairs > 1) errors.push('more than one orthogonally-adjacent prime pair');

  return errors;
}

/**
 * Audits how well a finished queue spaces its numbers — the observable proxy
 * for the reuse-window intent (§5.2 rules 4/5, §17.3). Returns the smallest gap
 * (in days/puzzle-ids) between consecutive appearances, overall and for primes.
 * Larger is better; used by the generator to report queue quality.
 */
export function minReuseGap(puzzles: readonly Puzzle[]): {
  anyNumber: number;
  prime: number;
} {
  const lastNumber = new Map<number, number>();
  const lastPrime = new Map<number, number>();
  let anyMin = Infinity;
  let primeMin = Infinity;
  for (const p of puzzles) {
    const primeSet = new Set(p.primeIndices.map((i) => p.grid[i]!));
    for (const n of p.grid) {
      const prev = lastNumber.get(n);
      if (prev !== undefined) anyMin = Math.min(anyMin, p.id - prev);
      lastNumber.set(n, p.id);
    }
    for (const prime of primeSet) {
      const prev = lastPrime.get(prime);
      if (prev !== undefined) primeMin = Math.min(primeMin, p.id - prev);
      lastPrime.set(prime, p.id);
    }
  }
  return {
    anyNumber: Number.isFinite(anyMin) ? anyMin : 0,
    prime: Number.isFinite(primeMin) ? primeMin : 0,
  };
}
